/**
 * guides.js — task pages for send.intake.dental.
 *
 * These target the queries a dental office actually types when it has a file
 * in hand and needs it somewhere else: "how to send a cbct scan", "how to send
 * dicom files", "how to send dental x-rays to another office".
 *
 * ── The division of labour with marketing ──────────────────────────────────
 * intake.dental/how-to-send-a-cbct-scan answers "how do I get the file OUT of
 * my imaging software" — it names Romexis, Sidexis, CS 3D and walks the export.
 * These pages answer "how do I get it to the specialist", which is the half
 * that happens here, where the upload form is. Each links to the other at the
 * point its own answer runs out. Two of our pages must not chase one query, so
 * the titles lead with the half each actually owns.
 *
 * Rendered as real React routes (so the CTA is the live product, not a link to
 * it) and prerendered to static HTML by scripts/prerender.mjs — see that file
 * for why a React route alone cannot rank.
 */

export const GUIDES = [
  {
    slug: 'send-a-cbct-scan',
    metaTitle: 'How to Send a CBCT Scan to a Specialist — Free & HIPAA-Compliant',
    description:
      'Send a full CBCT volume to an oral surgeon, endodontist or orthodontist without email size limits. Encrypted in your browser, PIN-protected, no account for the recipient. Free for dental offices.',
    h1: 'How to send a CBCT scan to a specialist',
    lede:
      'A CBCT volume is the file most likely to defeat email — a few hundred megabytes to a couple of gigabytes, in a format most inboxes reject outright. Here is how to get one to a specialist in about a minute.',
    schemaName: 'Send a CBCT scan to a specialist',
    steps: [
      { name: 'Export the volume from your imaging software', text: 'Export as DICOM (a folder of .dcm files) or the viewer bundle your system produces. If you are unsure where that lives in Romexis, Sidexis or CS 3D, the export guide linked below walks each one.' },
      { name: 'Zip the folder', text: 'A CBCT export is a directory of hundreds of slices. Zip it so it travels as a single file and arrives in one piece.' },
      { name: 'Drop it in and add the specialist', text: 'Up to 10 GB per file. Enter the specialist’s email and set a 4-digit PIN.' },
      { name: 'Text them the PIN', text: 'The link goes by email, the PIN does not — that separation is what keeps a forwarded inbox from becoming a disclosure.' },
    ],
    faqs: [
      { q: 'How do I send a CBCT scan that is too large to email?', a: 'Email attachment limits are typically 20–25 MB, and a CBCT volume is routinely hundreds of megabytes to a couple of gigabytes, so it will not fit. Send handles up to 10 GB per file: the specialist receives a link rather than an attachment, so nothing has to squeeze through either mail server.' },
      { q: 'Does the specialist need an account to open it?', a: 'No. They click the link, enter the 4-digit PIN you gave them separately, and download. No account, no software, no app.' },
      { q: 'Is it HIPAA-compliant to send a CBCT this way?', a: 'The transfer is encrypted in transit and at rest, access needs a PIN sent over a separate channel, links expire automatically, and a BAA is included. That is a materially different posture from a plain email attachment, which is neither encrypted end to end nor covered by a BAA with most consumer mail providers.' },
      { q: 'What format should I send — DICOM or the viewer bundle?', a: 'DICOM is the safest default because any specialist can open it in their own software. Some systems also export a self-contained viewer bundle, which is convenient but ties the recipient to that vendor’s viewer. If you are unsure, send the DICOM.' },
      { q: 'How long does the specialist have to download it?', a: 'Links expire after 7 days by default, and you can revoke one at any time — files are deleted immediately when you do. You get a notification when the package is opened.' },
    ],
    related: { href: 'https://intake.dental/how-to-send-a-cbct-scan', label: 'Not sure how to export the volume? Romexis, Sidexis, CS 3D and more →' },
  },
  {
    slug: 'send-dicom-files',
    metaTitle: 'How to Send DICOM Files Securely (Free for Dental Offices)',
    description:
      'Send DICOM studies to another office or specialist without a PACS, an FTP server, or a file-size limit. Browser-encrypted, PIN-protected, no account required on the receiving end.',
    h1: 'How to send DICOM files securely',
    lede:
      'DICOM is the format every imaging system speaks and almost no email server tolerates. You do not need a PACS or an FTP account to move a study — you need somewhere the whole folder can go at once.',
    schemaName: 'Send DICOM files securely',
    steps: [
      { name: 'Export the study as DICOM', text: 'Most systems export a folder of .dcm files, sometimes with a DICOMDIR index. Keep the folder intact — the slices are meaningless separated from each other.' },
      { name: 'Zip the whole folder', text: 'One file, structure preserved. This is the step people skip, and it is why studies arrive unopenable.' },
      { name: 'Upload and name the recipient', text: 'Drag the zip in, enter their email, set a PIN.' },
      { name: 'Send the PIN another way', text: 'Text or call it. Keeping the key off the same channel as the link is the point.' },
    ],
    faqs: [
      { q: 'Can I email DICOM files?', a: 'Rarely successfully. A single study routinely exceeds attachment limits, many mail servers strip or quarantine .dcm and .zip attachments, and consumer email is not covered by a BAA. A link with a separate PIN avoids all three problems.' },
      { q: 'Do I need a PACS to share DICOM with another office?', a: 'No. A PACS is for storing and querying imaging inside an organisation. Sending one study to one specialist is a transfer problem, not a storage problem — you just need the file to arrive intact and privately.' },
      { q: 'Will the receiving office be able to open it?', a: 'Any dental or medical imaging software reads DICOM, which is why it is worth sending rather than a proprietary viewer bundle. Zip the folder so the slices and index stay together.' },
      { q: 'Is there a file-size limit?', a: 'Up to 10 GB per file, and multiple files per transfer — enough for a full CBCT volume or a multi-series study.' },
    ],
    related: { href: '/send-a-cbct-scan', label: 'Sending a CBCT specifically? Start here →', internal: true },
  },
  {
    slug: 'send-dental-x-rays',
    metaTitle: 'How to Send Dental X-Rays to Another Office — Securely & Free',
    description:
      'Send dental x-rays, panoramic images and full-mouth series to another dental office or specialist. Encrypted, PIN-protected, expiring links, no account needed on the other end.',
    h1: 'How to send dental x-rays to another office',
    lede:
      'Referrals, transfers and second opinions all stall in the same place: the images. Here is how to get radiographs to another office without printing, burning a disc, or hoping an attachment goes through.',
    schemaName: 'Send dental x-rays to another office',
    steps: [
      { name: 'Export the images', text: 'JPEG or PNG is fine for bitewings, periapicals and panoramic images. For a CBCT or a full 3D study, export DICOM instead.' },
      { name: 'Add them to a transfer', text: 'Multiple files per transfer, so a full-mouth series travels as one package rather than fourteen attachments.' },
      { name: 'Enter the receiving office and set a PIN', text: 'They need no account — just the link and the PIN.' },
      { name: 'Send the PIN separately', text: 'A text or a phone call. Never in the same email as the link.' },
    ],
    faqs: [
      { q: 'Can I just email dental x-rays?', a: 'You can attach small images, but it is worth knowing what you are accepting: ordinary email is not encrypted end to end, most consumer providers will not sign a BAA, and the images sit in both mailboxes indefinitely. A link that expires, with a PIN sent separately, removes all three.' },
      { q: 'How do I send a full-mouth series?', a: 'Add every image to one transfer rather than sending them one at a time. The recipient gets a single package, which is also how they will want to read it.' },
      { q: 'What if the other office uses different software?', a: 'Standard image formats and DICOM are readable by every dental system, which is why exporting to those beats sending a proprietary viewer file. Nothing needs to be installed on their end.' },
      { q: 'Can I send x-rays to a patient?', a: 'For the patient’s own copy, a Dental Passport is the better route — the patient is identity-verified and controls the record afterwards, rather than a chart landing in whatever address was typed. Office-to-office transfers are what this tool is for.' },
    ],
    related: { href: '/send-dicom-files', label: 'Sending a 3D study or DICOM? Start here →', internal: true },
  },
];

export const GUIDE_BY_SLUG = Object.fromEntries(GUIDES.map((g) => [g.slug, g]));

/**
 * Schema builders live here so the React component and the prerenderer emit
 * IDENTICAL structured data. Built once, used twice: <JsonLd> injects it at
 * runtime, scripts/prerender.mjs writes it into the static head — and because
 * the ids match, the runtime copy replaces the static one instead of doubling
 * it. (A useEffect never runs during renderToStaticMarkup, so without the head
 * copy these pages would ship no HowTo or FAQ schema at all.)
 */
export function howToSchema(g) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: g.schemaName,
    description: g.description,
    totalTime: 'PT2M',
    estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
    step: g.steps.map((s, i) => ({
      '@type': 'HowToStep', position: i + 1, name: s.name, text: s.text,
    })),
  };
}

export function faqSchema(g) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: g.faqs.map((f) => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
